import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormQuill from "@babackapp/react-hook-forms-components/form-quill-input";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";
import { TranslatePortalCustomTextButton } from "../portal-wording/translate-portal-custom-text-button.tsx";

export const customErrorKeys = [
  "countryNotAvailable",
  "notCompliant",
  "notCompliantPOS",
  "orderExpired",
  "needValidationText",
  "notFullFilled",
] as const;

export const customMessagesKeys = [
  "headerMessage",
  "legalDoc",
  "articleSelectionMessage",
  "articleSelectionSubtitle",
  "receiptHeaderInfo",
  "formAddress",
  "confirmationMessage",
  "endProcess",
  "endProcessWithoutPhysicalReturn",
  "reasonStepInfo",
  "resolutionHeaderInfo",
  "carrierSelectionMessage",
  "checkoutMessage",
] as const;

export const retractationKeys = ["retractationLabel"] as const;

export const customKeys = [
  ...customErrorKeys,
  ...customMessagesKeys,
  ...retractationKeys,
] as const;

export type CustomKey = (typeof customKeys)[number];

const CustomMessagesSettingsSchema = z.object({
  values: z.array(
    z.object({
      lang: z.string(),
      text: z.string(),
    }),
  ),
});

type CustomMessagesSettingsFormValues = z.infer<
  typeof CustomMessagesSettingsSchema
>;

type CustomMessagesSettingsFormProps = {
  customKey: CustomKey;
  shopId?: string;
  languages?: string[];
  plain?: boolean;
  onSubmit: (data: CustomMessagesSettingsFormValues["values"]) => void;
  defaultValues?: {
    values: CustomMessagesSettingsFormValues["values"];
  };
};

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

function TranslateFormButton({
  shopId,
  languages,
}: {
  shopId: string;
  languages: string[];
}) {
  const { control, watch, setValue } =
    useFormContext<CustomMessagesSettingsFormValues>();
  const { fields } = useFieldArray({ control, name: "values" });
  const values = watch("values");

  const normalizedValues = values.map((v) => ({
    ...v,
    text: stripHtml(v.text) ? v.text : "",
  }));

  return (
    <TranslatePortalCustomTextButton
      shopId={shopId}
      values={normalizedValues}
      languages={languages}
      onTranslated={(translated) => {
        fields.forEach((field, index) => {
          if (translated[field.lang] != null) {
            setValue(
              `values.${index}`,
              { lang: field.lang, text: translated[field.lang] },
              { shouldDirty: true },
            );
          }
        });
      }}
    />
  );
}

function CustomMessagesSettingsFormFields({ plain }: { plain?: boolean }) {
  const { control } = useFormContext<CustomMessagesSettingsFormValues>();

  const valuesFields = useFieldArray({
    control: control,
    name: "values",
  });

  return valuesFields.fields.map((field, index) => (
    <FormField
      key={field.id}
      control={control}
      name={`values.${index}`}
      render={({ field }) =>
        plain ? (
          <FormInput
            label={field.value.lang}
            value={field.value.text}
            onChange={(e) =>
              field.onChange({
                lang: field.value.lang,
                text: e.target.value,
              })
            }
          />
        ) : (
          <FormQuill
            label={field.value.lang}
            value={field.value.text}
            onChange={(value) =>
              field.onChange({
                lang: field.value.lang,
                text: value,
              })
            }
          />
        )
      }
    />
  ));
}

export function CustomMessagesSettingsForm({
  defaultValues,
  onSubmit,
  shopId,
  languages,
  plain,
}: CustomMessagesSettingsFormProps) {
  const form = useForm<CustomMessagesSettingsFormValues>({
    resolver: zodResolver(CustomMessagesSettingsSchema),
    defaultValues,
  });

  const _onSubmit = (data: CustomMessagesSettingsFormValues) => {
    return onSubmit(data.values);
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col justify-start gap-8 mt-8"
        onSubmit={form.handleSubmit(_onSubmit)}
      >
        {shopId && languages && languages.length > 1 && (
          <div className="flex justify-end">
            <TranslateFormButton shopId={shopId} languages={languages} />
          </div>
        )}
        <CustomMessagesSettingsFormFields plain={plain} />

        <FormSaveButton />
      </form>
    </Form>
  );
}
